/**
 * TypeScript types for Block Grid Layout from Umbraco
 */

/**
 * Base interface for content blocks with identity
 */
export interface ContentBlock {
  id: string;
  type: string;
  [key: string]: unknown;
}

/**
 * Represents the Block Grid Layout composition from Umbraco
 */
export interface BlockGridLayout {
  /**
   * The number of columns in the grid (typically 12)
   */
  gridColumns: number;

  /**
   * The collection of block grid items with their layout information
   */
  items: BlockGridItem[];
}

/**
 * Represents a single item in the block grid with layout positioning
 */
export interface BlockGridItem {
  /**
   * Unique identifier for this grid item
   */
  id: string;

  /**
   * The content type alias (e.g., 'richTextBlock', 'imageBlock')
   */
  type: string;

  /**
   * Number of columns this item spans (1-12)
   */
  columnSpan: number;

  /**
   * Number of rows this item spans
   */
  rowSpan: number;

  /**
   * The actual content of the block, built by its respective ViewModelBuilder
   */
  content: ContentBlock | null;

  /**
   * Nested areas within this block grid item (for complex layouts)
   */
  areas?: BlockGridArea[] | null;
}

/**
 * Represents a block grid area containing nested items
 */
export interface BlockGridArea {
  /**
   * The alias of the area
   */
  alias: string;

  /**
   * Number of columns this area spans
   */
  columnSpan: number;

  /**
   * Number of rows this area spans
   */
  rowSpan: number;

  /**
   * The collection of items within this area
   */
  items: BlockGridItem[];
}

/**
 * Props for block grid component
 */
export interface BlockGridProps {
  /**
   * The block grid layout data
   */
  layout: BlockGridLayout;

  /**
   * Optional function to render a specific block type
   * If not provided, will use default rendering
   */
  renderBlock?: (
    item: BlockGridItem,
    areaChildren?: React.ReactNode
  ) => React.ReactNode;

  /**
   * Optional CSS class name for the grid container
   */
  className?: string;
}

/**
 * Type guard to check if a composition has block grid layout
 */
export function hasBlockGridLayout(
  compositions: Record<string, unknown>
): compositions is { blockGridLayout: BlockGridLayout } {
  return (
    compositions !== null &&
    typeof compositions === "object" &&
    "blockGridLayout" in compositions &&
    compositions.blockGridLayout !== null &&
    typeof compositions.blockGridLayout === "object"
  );
}

/**
 * Type guard to check if a block grid item has areas
 */
export function hasAreas(
  item: BlockGridItem
): item is BlockGridItem & { areas: BlockGridArea[] } {
  return Array.isArray(item.areas) && item.areas.length > 0;
}
